// Typed gap answers with the ÖSD spelling tolerance (Modellsatz B2, Lösungen und
// Bewertung, Lesen Aufgabe 3 and 4): "Wenn das Wort durch die abweichende
// Schreibweise keine andere Bedeutung erhält, werden auch Lösungen mit
// orthografischen Fehlern als richtig gewertet. Dies betrifft Doppelkonsonanten/
// -vokale, k-ck und s-ß, … Groß- und Kleinschreibung sowie gleich oder ähnlich
// klingende Laute (möglich bei: ai-ei, ä-e, v-f, d-t …; nicht bei: a-ä, o-ö, u-ü).
// … Fehlende Trennstriche und Satzzeichen werden nicht als Fehler gewertet."
//
// Parts opt in with `spellingLenient: true`. examService (exam score),
// practiceGrading (practice score), ExamResultsPage and the paper answer sheet
// (review) all mark through here so they can never disagree.

const plain = (value) => String(value ?? '').trim().toLowerCase().replace(/[-.,;:!?]+$/, '').trim();

// Same sound, different spelling: the slips the ÖSD accepts. a/ä, o/ö and u/ü stay apart.
const sound = (value) => value
  .replace(/ß/g, 'ss')
  .replace(/ck/g, 'k')
  .replace(/ä/g, 'e')
  .replace(/ai/g, 'ei')
  .replace(/v/g, 'f')
  .replace(/dt/g, 't')
  .replace(/d/g, 't')
  .replace(/(.)\1+/g, '$1');

// Short words whose slip makes another word (das/dass, den/denn, in/inn …): "another
// meaning", so not accepted when the answer is one of these and the key is not.
const OTHER_WORDS = new Set([
  'das', 'dass', 'den', 'denn', 'dem', 'der', 'die', 'des', 'wen', 'wenn', 'man', 'mann',
  'in', 'im', 'ihn', 'ihm', 'ihr', 'an', 'am', 'um', 'ob', 'ab', 'so', 'zu', 'es', 'er',
  'sie', 'wie', 'wir', 'mit', 'bis', 'vom', 'von', 'aus', 'auf', 'und', 'oder', 'als', 'ist',
  'statt', 'stadt', 'seit', 'seid', 'wird', 'wirt', 'tot', 'tod', 'rat', 'rad',
]);

const acceptedOf = (key) => (Array.isArray(key) ? key : [key]).map(plain).filter(Boolean);

/** Exact match after case, spaces and a final punctuation mark / hyphen. */
export const isGapAnswerExact = (key, answer) => {
  const got = plain(answer);
  return got !== '' && acceptedOf(key).includes(got);
};

/** Exact match, or a spelling slip of the ÖSD's kinds that keeps the word's meaning. */
export const isGapAnswerLenient = (key, answer) => {
  const got = plain(answer);
  if (!got) return false;
  const accepted = acceptedOf(key);
  if (accepted.includes(got)) return true;
  if (OTHER_WORDS.has(got)) return false;
  return accepted.some((want) => sound(want) === sound(got));
};

export const isSpellingLenientPart = (part) => Boolean(part?.spellingLenient);
